import CartCountdownTimer from 'app/components/CartCountdownTimer/CartCountdownTimer';
import { CartSummary } from 'app/components/CartSummary';
import CartSummaryVoucher from 'app/components/CartSummaryVoucher/CartSummaryVoucher';
import LoadingSpinner from 'app/components/Common/LoadingSpinner';
import GuestTabContent from 'app/components/ContactInformation/GuestTabContent';
import PaymentMethods from 'app/components/PaymentMethods/PaymentMethods';
import usePaymentMethods from 'app/components/PaymentMethods/usePaymentMethods';
import CartStepCardSkeleton from 'app/components/Skeletons/Common/CartStepCardSkeleton';
import { Button } from 'app/components/ui/button';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { getIFrameReferrer, onResize } from 'utils/embed';
import { getEventSlug, getTicketsPageLink } from 'utils/events';
import { getCartTotal } from 'utils/getItemsTotal';
import { isRenderedInsideEmbed } from 'utils/isRenderedInsideEmbed';
import {
  selectCartCountdown,
  selectCartData,
  selectTotalPriceOfTopUpsFromCart,
  selectWalletCredit,
  selectWhichChecksIfThereAreAddonsOnTheCartEvent,
} from '../../../store/cartSlice/selectors';
import { CartSteps } from '../../components/CartSteps/Loadable';
import { selectTicketIdsFromQueryParams } from '../TicketSalePage/slice/selectors';

export function ContactInfoCartPage() {
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const { state } = useLocation();

  const [selectedMethod, setSelectedMethod] = useState<number>(0);
  const [referrer, setReferrer] = useState<string>('');

  const ticketIdsFromQueryParams = useSelector(selectTicketIdsFromQueryParams);
  const walletData = useSelector(selectWalletCredit);
  const { onGoing } = useSelector(selectCartCountdown);
  const eventHasAddons = useSelector(
    selectWhichChecksIfThereAreAddonsOnTheCartEvent,
  );
  const addonsStepShouldBeDisplayed = walletData || eventHasAddons;

  const {
    loading,
    uuid,
    selectedItems,
    selectedEvent,
    selectedEvent: { id, name, type },
    voucher: voucherData,
  } = useSelector(selectCartData);
  const { cartTotal } = useMemo(
    () => getCartTotal(selectedItems, selectedEvent, voucherData),
    [],
  );

  const totalPriceOfTopUps = useSelector(selectTotalPriceOfTopUpsFromCart);
  const cartIsFree = cartTotal === 0 && totalPriceOfTopUps.sum === 0;

  const { paymentMethods, isLoading: paymentMethodsAreLoading } =
    usePaymentMethods(cartIsFree);

  const setNewReferrer = newReferrer => {
    console.log('newReferrer ----- ', newReferrer);
    setReferrer(newReferrer);
  };

  useEffect(() => {
    if (isRenderedInsideEmbed) {
      getIFrameReferrer(setNewReferrer);
    }
  }, []);

  const paymentLink = `${
    process.env.REACT_APP_OVEIT_DASHBOARD_LINK
  }/cart/place_marketplace_order?id_order=${uuid}&mkp=1&payment_processor=${
    paymentMethods[selectedMethod]?.type
  }${referrer ? `&referrer=${referrer}` : ''}&language=${i18n.language}`;

  setTimeout(() => {
    onResize();
  }, 100);

  // useEffect(() => {
  //   if (checkIfUserIsAuthenticated(expirationDate) && refreshToken) {
  //     dispatch(getBillingInfoAction());
  //   }
  // }, [refreshToken, expirationDate]);

  const getPreviousStepLink = () => {
    if (addonsStepShouldBeDisplayed) {
      return `/event/${getEventSlug(name, id)}/buy-tickets/addons`;
    }

    let result = getTicketsPageLink(name, id, type);

    if (ticketIdsFromQueryParams) {
      result += `?tickets=${ticketIdsFromQueryParams}`;
    }

    return result;
  };

  return (
    <div className="page-container">
      <Helmet>
        <title>{t('cartSteps.pageTitle', { eventName: name })}</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="flex justify-end px-4 mt-4">
        <CartCountdownTimer />
      </div>
      <div className="section-container">
        <CartSteps />
        {loading ? (
          <CartStepCardSkeleton />
        ) : (
          <div className="cart-step-card">
            <>
              <div className="cart-step-card-content">
                <div className="lg:mt-0 lg:mr-[16px] lg:w-2/3">
                  <GuestTabContent paymentLink={paymentLink} />
                  {cartIsFree ? null : (
                    <div className="mt-[24px]" id="payment-methods">
                      <h2 className="title-sm mb-3 sm:mb-6">
                        {t('registrationPage.paymentMethodsSectionTitle')}
                      </h2>
                      {paymentMethodsAreLoading ? (
                        <LoadingSpinner />
                      ) : (
                        <PaymentMethods
                          paymentMethods={paymentMethods}
                          selectedMethod={selectedMethod}
                          setSelectedMethod={setSelectedMethod}
                        />
                      )}
                    </div>
                  )}
                </div>
                <div className="mt-3 sm:mt-4 lg:mt-0 h-fit lg:w-1/3">
                  <CartSummary />
                  <CartSummaryVoucher />
                </div>
              </div>
              <div className="cart-step-card-footer">
                {state?.hideBackButton ? (
                  <p></p>
                ) : (
                  <Button variant="link" asChild>
                    <Link
                      to={getPreviousStepLink()}
                      state={{
                        comesFromPreviousPage: !addonsStepShouldBeDisplayed,
                      }}
                    >
                      <ArrowLeft />
                      {t('common.back')}
                    </Link>
                  </Button>
                )}
                <Button
                  disabled={!onGoing || paymentMethodsAreLoading}
                  type="submit"
                  form="contact-form"
                >
                  {cartIsFree ? t('common.continue') : t('common.pay')}
                  <ArrowRight />
                </Button>
              </div>
            </>
          </div>
        )}
      </div>
    </div>
  );
}
